import { Component, inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { TranslateDirective } from '@ngx-translate/core';
import {
  Breakpoint,
  BreakpointState,
  ChatComponent,
  FabMenuComponent,
  FinePrintComponent,
  MediaQueryService,
  FooterComponent,
  GlobalNotificationBannerComponent,
  MenuComponent,
  MenuService,
  MobileMenuComponent,
  SystemStatusService,
  FinePrints,
  FinePrintService,
  ONE_TRUST_CONFIGURATION,
  OneTrustConfiguration
} from '@tdwb/core';
import { ResponsiveOptionComponent } from './responsive-option.component';

@Component({
  selector: 'td-wb-layout',
  templateUrl: 'layout.component.html',
  styleUrls: ['layout.component.scss'],
  imports: [
    MenuComponent,
    MobileMenuComponent,
    FabMenuComponent,
    GlobalNotificationBannerComponent,
    FinePrintComponent,
    FooterComponent,
    ChatComponent,
    ResponsiveOptionComponent,
    TranslateDirective
  ]
})

export class LayoutComponent {
  private readonly mediaQueryService = inject(MediaQueryService);
  private readonly systemStatusService = inject(SystemStatusService);
  private readonly menuService = inject(MenuService);
  private readonly finePrintService = inject(FinePrintService);
  private readonly oneTrustConfiguration = inject<OneTrustConfiguration | null>(ONE_TRUST_CONFIGURATION, { optional: true });

  // Reactive boundaries: RxJS sources from services are converted to signals so template reads
  // (via method calls below) stay OnPush-safe without manual markForCheck() calls.
  private readonly isSmallScreen: Signal<boolean> = toSignal(
    this.mediaQueryService.observe(Breakpoint.LT_MD).pipe(
      map((state: BreakpointState): boolean => state.matches)
    ),
    { initialValue: false }
  );

  protected readonly hasFinePrints: Signal<boolean> = toSignal(
    this.finePrintService.onFinePrintUpdate.pipe(
      map((finePrints: FinePrints): boolean => Object.keys(finePrints).some((key: string): boolean => finePrints[key].length > 0))
    ),
    { initialValue: false }
  );

  private readonly systemAvailable: boolean = this.systemStatusService.isSystemAvailable();

  get showManagePreferencesLink(): boolean {
    return this.oneTrustConfiguration?.oneTrustEnabled === true;
  }

  showDesktopMenu(): boolean {
    return !this.isSmallScreen();
  }

  showMobileMenu(): boolean {
    return this.isSmallScreen() && this.isLocalMenuEnabled();
  }

  showFabMenu(): boolean {
    return this.isSmallScreen() && this.isLocalMenuEnabled();
  }

  /**
   * This function returns enabled state of local menu
   *
   * @returns Returns true if local menu is enabled. Otherwise, false.
   */
  isLocalMenuEnabled(): boolean {
    return this.systemAvailable && this.menuService.isEnabled();
  }

  isFooterHidden(): boolean {
    return this.menuService.isFooterHidden();
  }
}
